/**
 * NutritionPendingDemo — the nutrition clinic's "how does this work" demo
 * for פעולות ממתינות (Round 3 / Phase 23).
 *
 * A nutrition coach has no 3-stage appointment loop, so the therapy demo
 * (PendingActionsDemo) is meaningless here. This one renders a SAMPLE
 * "דוח אכילה לסקירה" item with the exact `nps-*` markup a real one uses,
 * and Lenny explains where such items come from and what the coach does
 * with them. Every click stays local: nothing touches Firestore, RTK Query
 * or activity logs.
 */

import { useState } from "react";
import { ArrowLeft, ForkKnife, PencilSimple, X } from "@phosphor-icons/react";
import lennyIcon from "../../../assets/lenny-icon-512.jpg";
import MacroIcon from "../../nutrition/MacroIcon";
import { formatHebrewDate } from "../../../utils/dateUtils";
import "./PendingActionsDemo.css";
import "../NutritionPendingSection.css";
import "./NutritionPendingDemo.css";

const SAMPLE_GOAL = { calories: 1800, carbohydrates: 200, protein: 120, fat: 60 };
const SAMPLE_TOTALS = { calories: 1640, carbohydrates: 172, protein: 108, fat: 61 };

const MACROS = [
  { key: "calories", label: "קלוריות", unit: "קק״ל" },
  { key: "carbohydrates", label: "פחמימות", unit: "ג׳" },
  { key: "protein", label: "חלבון", unit: "ג׳" },
  { key: "fat", label: "שומן", unit: "ג׳" },
];

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

function NutritionPendingDemo({ onClose }) {
  const [opened, setOpened] = useState(false);
  const sampleDate = yesterday();

  return (
    <div className="pad-root npd-root" role="list">
      <div className="pad-coach" role="note">
        <img src={lennyIcon} alt="" className="pad-coach-avatar" />
        <div className="pad-coach-body">
          <div className="pad-coach-head">
            <span className="pad-coach-name">לני</span>
            <span className="pad-coach-title">
              {opened ? "מה עושים עם דוח אכילה" : "ככה נראית פעולה ממתינה אצל מאמן/ת תזונה"}
            </span>
          </div>
          {opened ? (
            <p className="pad-coach-text">
              במרכז הליווי, בלשונית <strong>דוחות אכילה</strong>, רואים את כל מה
              שהלקוח/ה רשם/ה אתמול — ארוחה אחרי ארוחה, עם הערכים שחושבו. אפשר{" "}
              <strong>לתקן ערך</strong>, <strong>להוסיף ארוחה</strong> שנשכחה,{" "}
              <strong>לכתוב הערה ודירוג</strong> ולסמן <strong>״נסקר״</strong> —
              ואז הפריט יוצא מהרשימה הזו.
            </p>
          ) : (
            <p className="pad-coach-text">
              דוחות אכילה מגיעים לכאן <strong>אוטומטית כל בוקר</strong>, עבור
              לקוחות שרושמים מה אכלו בעמוד האישי שלהם. כאן סוקרים, מתקנים ומסמנים
              כנסקר. הפריט שלמטה הוא <strong>דוגמה</strong> — לחצו עליו כדי לראות מה
              קורה.
            </p>
          )}
        </div>
        <button
          type="button"
          className="pad-coach-close"
          onClick={onClose}
          aria-label="יציאה מההדגמה"
          title="יציאה מההדגמה"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      <section className="nps-group npd-group">
        <h4 className="nps-group-title">
          <ForkKnife size={16} weight="fill" />
          דוחות אכילה לסקירה
          <span className="nps-count">1</span>
          <span className="pad-badge npd-badge">דוגמה</span>
        </h4>

        <div className="nps-list" role="list">
          <button
            type="button"
            role="listitem"
            className={`nps-item npd-item ${opened ? "" : "pad-pulse-soft"}`}
            onClick={() => setOpened(true)}
          >
            <span className="nps-item-main">
              <span className="nps-item-title">לקוח/ה לדוגמה</span>
              <span className="nps-item-sub">
                {formatHebrewDate(sampleDate)} · {SAMPLE_TOTALS.calories} קק״ל
                {" · "}לחיצה פותחת את הסקירה
              </span>
            </span>
            <span className="nps-badge nps-badge--edited">
              <PencilSimple size={12} weight="fill" />
              הלקוח/ה שינה/תה ערכים
            </span>
          </button>
        </div>

        {opened && (
          <div className="npd-preview" role="note">
            <span className="npd-preview-title">מה שרואים בסקירה — סיכום היום מול היעדים</span>
            <div className="npd-preview-grid">
              {MACROS.map((macro) => {
                const actual = SAMPLE_TOTALS[macro.key];
                const goal = SAMPLE_GOAL[macro.key];
                const percent = Math.round((actual / goal) * 100);
                return (
                  <div className="npd-preview-cell" key={macro.key}>
                    <span className="npd-preview-label">
                      <MacroIcon macro={macro.key} size={13} />
                      {macro.label}
                    </span>
                    <span className="npd-preview-value">
                      <strong>{actual}</strong>
                      <span className="npd-preview-goal">/ {goal} {macro.unit}</span>
                    </span>
                    <span className={`npd-preview-pct ${percent > 100 ? "over" : ""}`}>
                      {percent}% מהיעד
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="pad-done-actions">
              <button type="button" className="pad-btn pad-btn-primary" onClick={onClose}>
                הבנתי, סגירת ההדגמה
                <ArrowLeft size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default NutritionPendingDemo;
